// src/features/auth/authSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { setHeaderToken } from "../system/api";
import { clear, getUser, setUser } from "../system/storage";
import {fetchOwnSupportPermissions} from "../system/support-access-api";

const logoutUser = () => {
    clear();
};

const getAdminLoginURL = () => {
    const baseURL = api.defaults.baseURL || window.location.origin;
    return new URL('../admin/login', baseURL).toString();
};

const getAdminMeURL = () => {
    const baseURL = api.defaults.baseURL || window.location.origin;
    return new URL('../admin/users/me', baseURL).toString();
};

const normalizeUser = (data) => {
    const payload = data?.data || data;
    const token = payload?.jwt || payload?.token;

    if (token && payload?.user) {
        return {...payload.user, jwt: token};
    }

    if (token) {
        return {...payload, jwt: token};
    }

    return payload;
};

// Support permissions: a 401/403 means "none" (fail closed, e.g. revoked). A network error or a server restart
// must not lock an admin out, so it is retried once and then the same account's last known permissions are kept.
const loadSupportPermissions = async (userId, previousUser) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            return {permissions: await fetchOwnSupportPermissions(), permissionsError: null};
        } catch (error) {
            const status = error?.response?.status;
            if ([401, 403].includes(status)) return {permissions: [], permissionsError: null};
            if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 1200));
            else {
                const keep = previousUser && String(previousUser.id) === String(userId) && Array.isArray(previousUser.permissions);
                return {permissions: keep ? previousUser.permissions : [], permissionsError: status || "network"};
            }
        }
    }
    return {permissions: [], permissionsError: "unknown"};
};

// --------- THUNKS ---------

export const check = createAsyncThunk('auth/check', async (_, thunkAPI) => {
    const storedUser = getUser();

    if (!storedUser?.jwt) {
        setHeaderToken();
        return null;
    }

    setHeaderToken(`Bearer ${storedUser.jwt}`);

    try {
        const {data} = await api.get(getAdminMeURL());
        const currentUser = data?.data || data;
        const access = await loadSupportPermissions(currentUser?.id, storedUser);
        const user = {...storedUser, ...currentUser, ...access, jwt: storedUser.jwt};
        setUser(user);
        return user;
    } catch (error) {
        setHeaderToken();
        if ([401, 403].includes(error?.response?.status)) clear();
        return thunkAPI.rejectWithValue(
            error?.response?.data?.error?.message || 'session-check-error'
        );
    }
});

export const login = createAsyncThunk('auth/login', async ({email, password}, thunkAPI) => {
    try {
        const {data} = await api.post(getAdminLoginURL(), {email, password});
        const user = normalizeUser(data);

        if (!user?.jwt) {
            return thunkAPI.rejectWithValue('signin-error');
        }

        setUser(user);
        setHeaderToken(`Bearer ${user.jwt}`);
        const access = await loadSupportPermissions(user?.id, null);
        const authenticatedUser = {...user, ...access};
        setUser(authenticatedUser);

        return authenticatedUser;
    } catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data?.error?.message || 'signin-error');
    }
});

export const logout = createAsyncThunk('auth/logout', async () => {
    await logoutUser();
    setHeaderToken();
});

// --------- SLICE ---------

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: getUser(),
        isAuthenticated: false,
        userChecked: false,
        loading: false,
        error: null,
        otpSent: undefined
    },
    reducers: {
        setOtpSent: (state, action) => {
            state.otpSent = action.payload;
        },
        saveUser: (state, action) => {
            state.user = action.payload;
            state.isAuthenticated = true;
            state.userChecked = true;
            state.error = null;
            setUser(action.payload);
            if (action.payload?.jwt) {
                setHeaderToken(`Bearer ${action.payload.jwt}`);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(check.pending, (state) => {
                state.loading = true;
            })
            .addCase(check.fulfilled, (state, action) => {
                state.user = action.payload;
                state.isAuthenticated = Boolean(action.payload?.jwt);
                state.userChecked = true;
                state.loading = false;
                state.error = null;
            })
            .addCase(check.rejected, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.userChecked = true;
                state.loading = false;
            })
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.user = action.payload;
                state.isAuthenticated = true;
                state.userChecked = true;
                state.loading = false;
                state.error = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.user = null;
                state.isAuthenticated = false;
                state.userChecked = true;
                state.loading = false;
                state.error = action.payload || 'signin-error';
            })
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.userChecked = true;
                state.loading = false;
                state.error = null;
            });
    }
});

export const { setOtpSent, saveUser } = authSlice.actions;

export default authSlice.reducer;
